import { ref, computed } from '@vue/composition-api'
import {
  formatNumber,
  getFileName,
  download,
  formatObject,
} from '@/@core/utils/utils'
import store from '@/store'
import {
  UPDATE_SHIPMENT,
} from '@/store/modules/shipment.module'
import { apiToastError, apiToastSuccess } from '@/@core/utils/toast'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import constants, { FILE_TYPES } from '@/constants'
import analytics from '@/@core/utils/analytics'
import { UPLOAD_DOCUMENTS } from '@/store/modules/common.module'

const { round } = utils

const {
  KP_DATE_FORMAT,
  CURRENCY_CONVERSIONS,
  SHIPMENT_STATUS,
} = c

export function useShipmentAction(shipmentRef) {
  // State
  const invoiceFile = ref(null)
  const invoiceAmount = ref(null)
  const currency = ref(CURRENCY_CONVERSIONS.USD.LABEL)
  const invoiceQuantity = ref(null)
  const invoiceAmountInUSD = ref(null)
  const exchangeRate = ref(null)
  const invoiceFileLocation = ref(null)
  const invoiceUrl = ref('')
  const isUploading = ref(false)
  const isVisible = ref(false)
  const shippedDate = ref(null)

  // Computeds
  const isBrand = computed(() => store.getters.isBrand)

  const isValid = computed(() => (
    invoiceFile.value
    && invoiceAmount.value > 0
    && invoiceQuantity.value > 0
  ))

  const getInvoiceAmountInUSDwithSymbol = computed(() => {
    if (invoiceAmount.value <= 0) {
      return null
    }
    return formatNumber(
      invoiceAmount.value * CURRENCY_CONVERSIONS.USD[currency.value],
      2,
    )
  })

  const getAmountInUSD = computed(() => {
    if (invoiceAmount.value <= 0) {
      return 0
    }
    return round(invoiceAmount.value, CURRENCY_CONVERSIONS.USD[currency.value])
  })

  const isDispute = computed(() => {
    const shipment = shipmentRef.value
    const isQuantityMatched = Number(invoiceQuantity.value) === shipment.totalQuantity
    const isPriceMatched = Number(getAmountInUSD.value) >= round((shipment.totalPriceWithVatForBrand || shipment.totalPrice), 0.98) && Number(getAmountInUSD.value) <= round((shipment.totalPriceWithVatForBrand || shipment.totalPrice), 1.02)
    return isValid.value && !(isQuantityMatched && isPriceMatched)
  })

  const isUpdateInvoiceEnabled = computed(() => !!shipmentRef.value.totalPriceFromBrand)

  // Methods
  function onDropInvoiceFile(e) {
    const droppedFile = e.dataTransfer.files[0]
    invoiceFile.value = droppedFile
  }

  function initInvoiceUrl() {
    if (shipmentRef.value?.invoiceUrl) {
      invoiceUrl.value = shipmentRef.value.invoiceUrl
    }
  }

  async function uploadInvoice() {
    isUploading.value = true
    try {
      if (invoiceFile.value instanceof File) {
        invoiceFileLocation.value = await store.dispatch(
          UPLOAD_DOCUMENTS,
          { fileType: FILE_TYPES.INVOICE_BY_BRAND, file: invoiceFile.value },
        )
      }
      const payload = {
        invoiceUrl: invoiceFileLocation.value,
        currencyFromBrand: currency.value,
        totalQuantityFromBrand: Number(invoiceQuantity.value),
        totalPriceFromBrand: Number(invoiceAmount.value),
      }

      const res = await store.dispatch(UPDATE_SHIPMENT, { shipmentId: shipmentRef.value._id, payload })
      apiToastSuccess(res.data.message)
      isUploading.value = false
      trackUploadShipmentDocuments()
      return { success: true }
    } catch (err) {
      isUploading.value = false
      apiToastError(err)
      return { success: false }
    }
  }

  function clearForm() {
    invoiceFile.value = null
    invoiceAmount.value = null
    currency.value = CURRENCY_CONVERSIONS.USD.LABEL
    invoiceQuantity.value = null
    invoiceAmountInUSD.value = null
    exchangeRate.value = null
    invoiceFileLocation.value = null
    isVisible.value = false
  }

  function setEditableFields() {
    invoiceFile.value = {
      name: getFileName(FILE_TYPES.INVOICE_BY_BRAND, invoiceUrl.value || ''),
    }
    invoiceFileLocation.value = invoiceUrl.value || ''
    currency.value = shipmentRef.value.currencyFromBrand
    invoiceQuantity.value = shipmentRef.value.totalQuantityFromBrand
    invoiceAmount.value = shipmentRef.value.totalPriceFromBrand
  }

  function trackUploadShipmentDocuments(shippedDateVal = null) {
    const shipment = shipmentRef.value
    const trackData = {
      noOfProductsInShipment: shipment.products?.length || 0,
      collectionName: shipment.collectionName,
      logisticsTerms: shipment.logisticsTerms,
    }
    const action = shippedDateVal ? constants.TRACKS.ACTIONS.BRAND_MARKS_SHIPMENT_COLLECTED : constants.TRACKS.ACTIONS.BRAND_UPLOADS_SHIPMENT_DOCUMENTS
    analytics.track(action, formatObject(trackData))
  }

  return {
    // State
    invoiceFile,
    invoiceAmount,
    currency,
    invoiceQuantity,
    invoiceAmountInUSD,
    exchangeRate,
    invoiceFileLocation,
    invoiceUrl,
    isUploading,
    isVisible,
    shippedDate,

    // Computeds
    isBrand,
    isValid,
    getInvoiceAmountInUSDwithSymbol,
    getAmountInUSD,
    isDispute,
    isUpdateInvoiceEnabled,

    // Methods
    onDropInvoiceFile,
    initInvoiceUrl,
    uploadInvoice,
    clearForm,
    setEditableFields,
    trackUploadShipmentDocuments,

    // Constants & utilities
    KP_DATE_FORMAT,
    CURRENCY_CONVERSIONS,
    SHIPMENT_STATUS,
    FILE_TYPES,
    formatNumber,
    getFileName,
    download,
  }
}
